import { notification } from 'antd';
import axios from 'axios';
import { logoutUser } from '../thunks/userAuthThunks.js';
import { handleInvalidToken } from '../thunks/authThunks.js';
import { auth } from '../api/index.js';

let instance = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

const errorHandler = (err) => Promise.reject(err.response?.data || err);

const API = {
    setToken: (token) => {
        instance.defaults.headers.common.Authorization = 'JWT ' + token;
    },
    get: (url) =>
        instance
            .get(url)
            .then((res) => res.data)
            .catch(errorHandler),

    post: (url, data) =>
        instance
            .post(url, data)
            .then(({ data }) => data)
            .catch(errorHandler),

    put: (url, data) =>
        instance
            .put(url, data)
            .then(({ data }) => data)
            .catch(errorHandler),

    delete: (url, data) =>
        instance
            .delete(url, { data })
            .then((res) => res.data)
            .catch(errorHandler),
};

let csrfToken = null;

export const setCsrfToken = (token) => {
    csrfToken = token;
};

export const setupInterceptors = (store) => {
    instance.interceptors.request.use(
        (config) => {
            const method = (config.method || '').toLowerCase();
            if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(method)) {
                config.headers['X-CSRF-Token'] = csrfToken;
            }
            return config;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const { response, config } = error;

            if (!response) {
                notification.error({
                    message: 'Connection error',
                    description: 'Unable to reach server. Please re-login.',
                });
                store.dispatch(logoutUser());
                return Promise.reject(error);
            }

            if (response.data.message === 'Secret key is not valid') {
                store.dispatch(logoutUser());
            }

            const status = response.status;
            const url = config.url || '';
            //logout user on 401 errors and try to refresh token
            if (status === 401) {
                if (url.includes('/auth/me') || url.includes('/auth/login') || config._isRetry) {
                    return Promise.reject(error);
                }

                config._isRetry = true;

                if (!store.getState().auth.loadingSecret) {
                    try {
                        const user = await auth.getUserByCookie();
                        store.dispatch(handleInvalidToken(user));
                        config.headers.Authorization = `JWT ${user.token}`;
                        return instance(config); // retry original request
                    } catch {
                        // fallback if refresh fails
                        notification.error({
                            message: 'Error',
                            description: 'Session expired, please log in again',
                        });
                        store.dispatch(logoutUser());
                        return Promise.reject(error);
                    }
                }

                // JWT expiration check in localStorage
                const userString = localStorage.getItem('user');
                if (userString) {
                    try {
                        const user = JSON.parse(userString);
                        const tokenParts = user.token.split('.');
                        if (tokenParts.length === 3) {
                            const decodedJwt = JSON.parse(atob(tokenParts[1]));
                            if (decodedJwt.exp * 1000 < Date.now()) {
                                store.dispatch(logoutUser());
                                notification.error({
                                    message: 'Token error',
                                    description: 'Token expired, please re-enter your credentials',
                                });
                            }
                        }
                    } catch (e) {
                        console.error('JWT parse error:', e);
                    }
                }

                return Promise.reject(error);
            }

            if (status === 404) {
                notification.error({ message: 'ERROR', description: 'Something went wrong' });
            }

            if (status !== 401 && status !== 404) {
                console.error(error);
            }

            return Promise.reject(error);
        },
    );
};

export default API;
